import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useHttpError from '../../../hooks/useHttpError/useHttpError';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import useModal from '../../../hooks/useModal/useModal';
import { Device, DeviceUpdateRequest, Sim, SimUpdateRequest } from '../../../models/UserDevicesModels';
import UsersService from '../../../services/Users/UsersService';

function UseDeviceDetails(id: string | undefined) {
  const [device, setDevice] = useState<Device | null>(null);
  const [sims, setSims] = useState<Sim[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const [t] = useTranslation();
  const { getHttpReasonFromList } = useHttpError();
  const modal = useModal();
  const navigate = useNavigate();

  /**
   * Fetches the device details by id
   */
  const fetchDeviceDetails = () => {
    setIsLoading(true);
    UsersService.api.fetchDeviceById(id as string).then(
      (response) => {
        setIsLoading(false);
        setDevice(response.data.device);
        setSims(response.data?.sims ? response.data.sims : []);
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.getDeviceError'), 'error');
      },
    );
  };

  /**
   * Updates the device on the remote server
   * @param d
   * @param setFormOpen
   */
  const updateDeviceDetails = (d: DeviceUpdateRequest, setFormOpen: (val: boolean) => void) => {
    setIsLoading(true);
    UsersService.api.updateDevice(d).then(
      (response) => {
        setIsLoading(false);
        setDevice(response.data.device);
        setSims(response.data?.sims ? response.data.sims : []);
        setFormOpen(false);
        snackBar.showSnackBar(t('pages.deviceDetails.updateDeviceSuccess'), 'success');
      },
      (res) => {
        setIsLoading(false);
        const error = getHttpReasonFromList(
          res.response.data,
          [3042, 3067, 3107, 3121, 3122, 3123, 3124, 3125, 3126, 3127, 3128],
          t('pages.deviceDetails.updateDeviceError'),
        );
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  /**
   * Updated the sim on the remote server
   * @param req
   * @param errorCallback
   */
  const updateSimDetails = async (req: SimUpdateRequest, errorCallback: () => void) => {
    setIsLoading(true);
    UsersService.api.updateSim(req).then(
      () => {
        fetchDeviceDetails();
        snackBar.showSnackBar(t('pages.userDetails.updateSimSuccess'), 'success');
        setIsLoading(false);
      },
      (res) => {
        errorCallback();
        setIsLoading(false);
        const error = getHttpReasonFromList(
          res.response.data,
          [3041, 3052, 3060, 3070, 3071, 3103, 3139],
          t('pages.userDetails.updateSimError'),
        );
        snackBar.showSnackBar(error, 'error');
      },
    );
  };

  /**
   * Deletes the device
   */
  const deleteDevice = async () => {
    setIsLoading(true);
    UsersService.api.deleteDevice(id as string).then(
      () => {
        navigate('/dashboard/unassignedReport');
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceSuccess'), 'success');
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceError'), 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Handler for when user presses the delete device
   * Checks if the schedulers contains the device
   */
  const deleteDeviceHandler = async () => {
    setIsLoading(true);
    UsersService.api.schedulerContainsDevice(id as string).then(
      (res) => {
        if (res.data.hasSchedulers) {
          modal.showModal({
            modalTitle: t('pages.deviceDetails.warning'),
            modalDescription: (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <span>{t('pages.deviceDetails.scheduledWarningMessage1')}</span>
                <span>{t('pages.deviceDetails.scheduledWarningMessage2')}</span>
              </Box>
            ),
            confirmationModal: true,
            confirmationButtonLabel: t('common.delete'),
            onAccept: (setOpen) => {
              setOpen(false);
              deleteDevice();
            },
            onDiscard: () => {
              setIsLoading(false);
            },
          });
        } else {
          deleteDevice();
        }
      },
      () => {
        snackBar.showSnackBar(t('pages.deviceDetails.deleteDeviceError'), 'error');
        setIsLoading(false);
      },
    );
  };

  React.useEffect(() => {
    if (id) {
      fetchDeviceDetails();
    } else {
      snackBar.showSnackBar(t('pages.deviceDetails.deviceIdNotProvided'), 'error');
    }
  }, [id]);

  return {
    device,
    sims,
    fetchDeviceDetails,
    updateDeviceDetails,
    isLoading,
    updateSimDetails,
    deleteDeviceHandler,
  };
}

export default UseDeviceDetails;
